#! /usr/bin/env node
/* eslint-disable no-console */

import 'source-map-support/register';
import './polyfills';
import * as semver from 'semver';
import * as perf from './commons/performance-logger';
import * as prepareRunner from './commons/prepareRunner';
import * as runnerFileCache from './commons/runnerFileCache';
import * as prepareRunnerAndTestimStartUtils from './commons/prepareRunnerAndTestimStartUtils';
import chalk from 'chalk';
import { EventEmitter } from 'node:events';
import { CLI_MODE } from './commons/constants';
import { registerProcessForCleanup } from './processHandler';
import { NoArgsError, ArgError } from './errors';
import { onExit, ignoreFailingTestsInExitCode } from './cli/onExit';
import { updateRemoteRunFailure } from './commons/testimServicesApi';
import { getLogger, setProxyUri, setProjectId, adjustLoggersFromFeatureFlags } from './commons/logger';
import { featureFlagsService } from './commons/featureFlagsService';
import { type RunnerOptions } from './runOptions';

const logger = getLogger('cli-entry');

declare global {
    /* eslint-disable no-var */
    /** Meta parameters defined globally in build-time */
    var meta: {
        readonly IS_LOCAL: false;
        readonly IS_RELEASE: boolean;
        readonly BRANCH_NAME: string;
    } | undefined;
    var caFileContent: Buffer | string | undefined;
    var proxyUri: string | undefined;
    var SuperagentProxy: typeof import('superagent-proxy') | undefined;
    /* eslint-enable no-var */
}

function checkNodeVersion(engineVersion: string) {
    if (!semver.satisfies(process.version, engineVersion)) {
        throw new ArgError(`Testim.io CLI supports Node.js ${engineVersion}. This condition was not satisfied with current version: ${process.version}`);
    }

    const currentMinimalNodeVersion = 20;
    const nextMinimalNodeVersion = 22;
    const majorVersion = Number(process.versions.node.split('.')[0]);
    // Release timeline: https://github.com/nodejs/Release/blob/main/README.md
    const ltsNodeEolDate = new Date('2026-04-30T00:00:00.000Z');
    const dateHasPassed = ltsNodeEolDate <= new Date();
    const formattedDate = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(ltsNodeEolDate);

    if (majorVersion < currentMinimalNodeVersion) {
        throw new ArgError(`Testim.io CLI supports Node.js ${currentMinimalNodeVersion} and above, please upgrade to a newer Node.js version`);
    }

    if (majorVersion < nextMinimalNodeVersion && dateHasPassed) {
        throw new ArgError(`Testim.io CLI supports Node.js ${nextMinimalNodeVersion} and above, please upgrade to a newer Node.js version`);
    }

    if (majorVersion < nextMinimalNodeVersion) {
        console.log(chalk.yellow(`Testim.io CLI will stop supporting Node.js < ${nextMinimalNodeVersion} on ${formattedDate}. please upgrade to a newer Node.js version.`));
    }
}

async function main() {
    console.log('Starting Testim.io CLI');

    // Lazy load some dependencies which are slow to load, so the UX is not of a slow starting CLI tool
    const utils = await import('./utils');
    perf.log('Starting Testim.io CLI');
    registerProcessForCleanup(onExit);

    try {
        checkNodeVersion(utils.getEnginesVersion());
    } catch (err) {
        console.log('Argument Error:', err.message);
        process.exit(1);
    }
    try {
        // load run options in this try/catch block to properly handle errors from argument parsing
        const options = await import('./runOptions');
        const { program } = options;
        /*
            Setup FF early, so we can use them to process args.
            Be careful not to use FF that are relying on custom properties, since we don't have enough context yet.
        */
        await featureFlagsService.fetch({ proxy: program.proxy });
        adjustLoggersFromFeatureFlags();

        const processedOptions = await options.process();
        perf.log('in main, after options.process');
        setProxyUri(global.proxyUri);

        EventEmitter.defaultMaxListeners = 'parallel' in processedOptions && processedOptions.parallel > 10 ? processedOptions.parallel * 3 : 30;
        setProjectId((processedOptions as RunnerOptions).project);
        runnerFileCache.setEncryptKey!('token' in processedOptions && typeof processedOptions.token === 'string' ? processedOptions.token : 'anonymous_encrypt_key');

        if (utils.isInstallLazyDepsMode(processedOptions)) {
            console.log('Lazy dependency installation started');
            const { installAllLazyDependencies } = await import('./commons/lazyRequire');
            return await installAllLazyDependencies();
        }
        if (utils.isLoginMode(processedOptions)) {
            return undefined;
        }
        if (utils.isCreatePrefetchedDataMode(processedOptions)) {
            await runnerFileCache.clear();
            await prepareRunner.initializeUserWithAuth(processedOptions);
            const { preloadTests } = await import('./commons/preloadTests');
            await preloadTests(processedOptions);
            if (!processedOptions.playerRequirePath && processedOptions.mode !== CLI_MODE.EXTENSION) {
                await prepareRunnerAndTestimStartUtils.preparePlayer(processedOptions.playerLocation, processedOptions.canary);
            }
            const res = await runnerFileCache.waitForSave();
            if (res.success) {
                console.log(`created prefetched data at ${runnerFileCache.getCacheFileLocation()}`);
            } else {
                console.error('failed to create prefetch data', res.error);
            }
            return undefined;
        }

        const testRunner = await import('./runner');
        if (utils.isTunnelOnlyMode(processedOptions)) {
            await testRunner.init(processedOptions as unknown as RunnerOptions);
            const { serveTunneling } = await import('./commons/testimTunnel');
            await serveTunneling(processedOptions);
            return undefined;
        }

        if (utils.isAgentMode(processedOptions)) {
            const { runAgentMode } = await import('./cliAgentMode');
            return runAgentMode(processedOptions);
        }

        if (processedOptions.saveRCALocally) {
            const { initServer } = await import('./services/localRCASaver');
            const { port } = await initServer(processedOptions);
            processedOptions.localRCASaver = `http://localhost:${port}`;
        }

        if (processedOptions.exitCodeIgnoreFailingTests) {
            ignoreFailingTestsInExitCode();
        }

        perf.log('right before testRunner.init/prepareRunner.prepare');
        await testRunner.init(processedOptions);
        const customExtensionLocalLocation = await prepareRunner.prepare(processedOptions);

        perf.log('right after testRunner.init/prepareRunner.prepare');
        return await testRunner.run(processedOptions, customExtensionLocalLocation);
    } catch (err) {
        if (err instanceof NoArgsError) {
            // display help by default
            return undefined;
        }
        const argsForRemoteRunFailure = utils.getArgsOnRemoteRunFailure();
        if (argsForRemoteRunFailure) {
            await updateRemoteRunFailure({ ...argsForRemoteRunFailure, error: err.message }).catch(() => { /* */ });
        }
        if (err instanceof ArgError) {
            console.log(chalk.red('Argument Error:', err.message));
            return err;
        }
        console.log('Error:', err.message);
        logger.error('runner ended with unexpected error', { err });
        return err;
    }
}

main().then(result => {
    if (Array.isArray(result) && result.length === 0) {
        console.log('No tests ran');
    }
    onExit(result);
});

